import React, { useCallback, useRef, useState } from "react";

import { format } from "date-fns";
import SvgCalendar from "icons/lib/Calendar";
import { color } from "minimum-ui/tokens";
import styled, { css } from "styled-components";
import { H4M } from "typography/headers";

import { useElementBox } from "../../hooks";

import { Button, Icon as UiIcon } from "minimum-ui/core";
import Menu from "./Menu";
import SingleFormMenu from "./SingleFormMenu";
import SingleMenu from "./SingleMenu";

type DatePickerCallback = (dates: [Date | null, Date | null]) => void;
type SingleDatePickerCallback = (date: Date | null) => void;

export interface DatePickerProps {
  /**
   * Current start date
   */
  startDate: Date | null;
  /**
   * Current end date
   */
  endDate?: Date | null;
  /**
   * Change handler for range dates
   */
  onChange?: DatePickerCallback;
  /**
   * Change handler for single date
   */
  onSingleChange?: SingleDatePickerCallback;
  /**
   * Alignment on page
   */
  rightAligned?: boolean;
  /**
   * Full width of the parent
   */
  fullWidth?: boolean;
  /**
   * Single date picker (no range)
   */
  single?: boolean;
  /**
   * Single date picker (no range) used in forms
   */
  form?: boolean;
  /**
   * Input field text value
   */
  placeholderText?: string;
  /**
   * Function to translate text
   */
  translate: (
    key: string,
    options?: Record<string, string | number | boolean>
  ) => string;
  v2?: boolean;
}

const formattedDates = (
  startDate: Date,
  endDate: Date,
  Label: React.ElementType
) => {
  return (
    <Label>
      {format(startDate, "dd MMM yyyy")} - {format(endDate, "dd MMM yyyy")}
    </Label>
  );
};

const singleFormattedDates = (startDate: Date, Label: React.ElementType) => {
  return <Label>{format(startDate, "dd MMM yyyy")}</Label>;
};

const DatePicker: React.FC<DatePickerProps> = ({
  startDate,
  endDate,
  onChange,
  onSingleChange,
  rightAligned = false,
  fullWidth,
  single,
  form,
  placeholderText,
  translate,
  v2 = false,
}) => {
  const [isOpen, setOpen] = useState(false);

  const onToggle = useCallback(() => {
    setOpen((o) => !o);
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const onApply = useCallback(
    (dates: [Date | null, Date | null]) => {
      onChange && onChange(dates);
    },
    [onChange]
  );

  const onSingleApply = useCallback(
    (date: Date | null) => {
      onSingleChange && onSingleChange(date);
    },
    [onSingleChange]
  );

  const picker = useRef<HTMLDivElement>(null);
  const position = useElementBox(picker, isOpen);

  const labelComponent = v2 ? React.Fragment : H4M;
  const label = single
    ? startDate
      ? singleFormattedDates(startDate, labelComponent)
      : placeholderText ??
        translate("selectDate", {
          namespace: "DEFAULT",
          count: 1,
        })
    : startDate && endDate
    ? formattedDates(startDate, endDate, labelComponent)
    : placeholderText ??
      translate("selectDate", {
        namespace: "DEFAULT",
        count: 2,
      });

  return (
    <DatePickerWrapper $rightAligned={rightAligned} $fullWidth={fullWidth}>
      {v2 ? (
        <div ref={picker}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onToggle}
            startIcon={<UiIcon icon="calendar" />}
          >
            {label}
          </Button>
        </div>
      ) : (
        <DatePickerLabel
          $fullWidth={fullWidth}
          $form={form}
          ref={picker}
          onClick={onToggle}
        >
          <Icon>
            <SvgCalendar width={12} height={13} />
          </Icon>

          <Label>{label}</Label>
        </DatePickerLabel>
      )}
      {!form && !single && (
        <Menu
          open={isOpen}
          onClose={onClose}
          top={position?.bottom ?? 0}
          left={position?.left ?? 0}
          right={position?.right ?? 0}
          rightAligned={rightAligned}
          onApply={onApply}
          startDate={startDate}
          endDate={endDate}
          translate={translate}
        />
      )}
      {!form && single && (
        <SingleMenu
          open={isOpen}
          onClose={onClose}
          top={position?.bottom ?? 0}
          left={position?.left ?? 0}
          right={position?.right ?? 0}
          rightAligned={rightAligned}
          onApply={onSingleApply}
          startDate={startDate}
          translate={translate}
        />
      )}
      {form && single && (
        <SingleFormMenu
          open={isOpen}
          onClose={onClose}
          top={position?.bottom ?? 0}
          left={position?.left ?? 0}
          right={position?.right ?? 0}
          rightAligned={rightAligned}
          onApply={onSingleApply}
          startDate={startDate}
          translate={translate}
        />
      )}
    </DatePickerWrapper>
  );
};

const DatePickerWrapper = styled.div<{
  $rightAligned: boolean;
  $fullWidth?: boolean;
}>`
  cursor: pointer;
  ${(props) =>
    props.$rightAligned &&
    css`
      margin-left: auto;
    `}
  ${(props) =>
    props.$fullWidth &&
    css`
      width: 100%;
    `}
`;

const DatePickerLabel = styled.div<{
  $fullWidth?: boolean;
  $form?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  background: var(--background, ${color.basic.white});
  border: ${(props) =>
    props.$form
      ? `1px solid color.neutral[200]`
      : `1px solid ${color.foreground.disabled}`};
   {
  }
  min-height: ${(props) => (props.$form ? "38px" : "none")};
  border-radius: 8px;
  padding: 4px 16px 4px 14px;
  gap: 8px;

  ${(props) =>
    props.$fullWidth &&
    css`
      width: 100%;
    `}
`;

const Icon = styled.div`
  padding-top: 1px;
  color: ${color.neutral[400]};
`;

const Label = styled(H4M).attrs({ as: "span" })`
  color: var(--text, ${color.neutral[400]});
  transition: color 0.3s;
`;

export default DatePicker;
